/**
 * DiagnosticsPage.jsx
 * 
 * Secure diagnostic page for automated API testing.
 * Access controlled via:
 * 1. Query parameter: ?agent-key=<VITE_AGENT_TEST_KEY>
 * 2. DevMode enabled in the app
 * 
 * Returns structured JSON for agent parsing when all tests complete.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDevMode } from '../contexts/DevModeContext';
import { Shield, CheckCircle, XCircle, Clock, RefreshCw, Lock } from 'lucide-react';
import { Spinner } from '../components/Animations';
import api from '../services/api';

// Agent test key - in production, set VITE_AGENT_TEST_KEY env var
const AGENT_TEST_KEY = import.meta.env.VITE_AGENT_TEST_KEY || null;

// Endpoints to test
const ENDPOINTS = [
    { name: 'Health', method: 'health', path: '/api/health' },
    { name: 'Kinks', method: 'getKinks', path: '/api/kinks', params: { limit: 1 } },
    { name: 'Sex Actions', method: 'getSexActions', path: '/api/sex-actions', params: { limit: 1 } },
    { name: 'Arousal Mechanics', method: 'getArousalMechanics', path: '/api/arousal-mechanics', params: { limit: 1 } },
    { name: 'Scenarios', method: 'getScenarios', path: '/api/scenarios', params: { limit: 1 } },
    { name: 'Gear', method: 'getGear', path: '/api/gear', params: { limit: 1 } },
    { name: 'Archetypes', method: 'getArchetypes', path: '/api/archetypes', params: { limit: 1 } },
];

export default function DiagnosticsPage() {
    const [searchParams] = useSearchParams();
    const { isDevMode } = useDevMode();
    const [results, setResults] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    const [testSummary, setTestSummary] = useState(null);

    // Security check
    const agentKey = searchParams.get('agent-key');
    const isAuthorized = isDevMode || agentKey === AGENT_TEST_KEY;

    // Run a single endpoint test
    const testEndpoint = async (endpoint) => {
        const startTime = performance.now();
        try {
            let data;
            if (endpoint.method === 'health') {
                data = await api.health();
            } else if (api[endpoint.method]) {
                data = await api[endpoint.method](endpoint.params || {});
            } else {
                throw new Error(`Method ${endpoint.method} not found`);
            }

            const duration = Math.round(performance.now() - startTime);
            return {
                name: endpoint.name,
                path: endpoint.path,
                status: 'success',
                statusCode: 200,
                duration,
                recordCount: data?.kinks?.length || data?.actions?.length || data?.mechanisms?.length ||
                    data?.scenarios?.length || data?.gear?.length || data?.archetypes?.length ||
                    (data?.status ? 1 : 0),
                sample: endpoint.method === 'health' ? data : null,
            };
        } catch (error) {
            const duration = Math.round(performance.now() - startTime);
            return {
                name: endpoint.name,
                path: endpoint.path,
                status: 'error',
                statusCode: error.message.includes('500') ? 500 :
                    error.message.includes('404') ? 404 : 0,
                duration,
                error: error.message,
            };
        }
    };

    // Run all tests
    const runAllTests = useCallback(async () => {
        setIsRunning(true);
        setResults([]);
        setTestSummary(null);

        const testResults = [];
        const startTime = performance.now();

        for (const endpoint of ENDPOINTS) {
            const result = await testEndpoint(endpoint);
            testResults.push(result);
            setResults([...testResults]);
        }

        const totalDuration = Math.round(performance.now() - startTime);
        const passed = testResults.filter(r => r.status === 'success').length;
        const failed = testResults.filter(r => r.status === 'error').length;

        const summary = {
            timestamp: new Date().toISOString(),
            totalTests: testResults.length,
            passed,
            failed,
            totalDuration,
            allPassed: failed === 0,
            results: testResults,
        };

        setTestSummary(summary);
        setIsRunning(false);

        // Log JSON for agent parsing
        console.log('AGENT_DIAGNOSTICS_RESULT:', JSON.stringify(summary, null, 2));
    }, []);

    // Auto-run tests when authorized and page loads
    useEffect(() => {
        if (isAuthorized && results.length === 0 && !isRunning) {
            runAllTests();
        }
    }, [isAuthorized, results.length, isRunning, runAllTests]);

    // Access denied view
    if (!isAuthorized) {
        return (
            <div className="min-h-screen bg-charcoal-900 flex items-center justify-center">
                <div className="text-center">
                    <Lock className="w-16 h-16 text-wine-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-cream-100 mb-2">Access Denied</h1>
                    <p className="text-cream-500">
                        This page requires authentication.
                    </p>
                    <p className="text-cream-600 text-sm mt-2">
                        Use <code className="bg-charcoal-800 px-2 py-1 rounded">?agent-key=&lt;key&gt;</code> or enable DevMode.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-charcoal-900 px-4 py-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <Shield className="w-8 h-8 text-wine-400" />
                        <div>
                            <h1 className="text-2xl font-bold text-cream-100">API Diagnostics</h1>
                            <p className="text-cream-500 text-sm">Agent Testing Dashboard</p>
                        </div>
                    </div>
                    <button
                        onClick={runAllTests}
                        disabled={isRunning}
                        className="flex items-center gap-2 px-4 py-2 bg-wine-600 hover:bg-wine-500 disabled:opacity-50 text-white rounded-lg transition-colors"
                    >
                        {isRunning ? <Spinner size="sm" /> : <RefreshCw className="w-4 h-4" />}
                        {isRunning ? 'Testing...' : 'Run Tests'}
                    </button>
                </div>

                {/* Summary Card */}
                {testSummary && (
                    <div className={`mb-6 p-4 rounded-xl border ${testSummary.allPassed
                        ? 'bg-emerald-900/20 border-emerald-500/30'
                        : 'bg-wine-900/20 border-wine-500/30'
                        }`}>
                        <div className="flex items-center gap-3 mb-2">
                            {testSummary.allPassed ? (
                                <CheckCircle className="w-6 h-6 text-emerald-400" />
                            ) : (
                                <XCircle className="w-6 h-6 text-wine-400" />
                            )}
                            <span className="font-semibold text-cream-100">
                                {testSummary.allPassed ? 'All Tests Passed' : 'Some Tests Failed'}
                            </span>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="text-cream-500">Total:</span>
                                <span className="ml-2 text-cream-100">{testSummary.totalTests}</span>
                            </div>
                            <div>
                                <span className="text-cream-500">Passed:</span>
                                <span className="ml-2 text-emerald-400">{testSummary.passed}</span>
                            </div>
                            <div>
                                <span className="text-cream-500">Failed:</span>
                                <span className="ml-2 text-wine-400">{testSummary.failed}</span>
                            </div>
                            <div>
                                <span className="text-cream-500">Duration:</span>
                                <span className="ml-2 text-cream-100">{testSummary.totalDuration}ms</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Results Table */}
                <div className="bg-charcoal-800/50 rounded-xl border border-charcoal-700 overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-charcoal-800 border-b border-charcoal-700">
                                <th className="px-4 py-3 text-left text-sm font-medium text-cream-400">Endpoint</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-cream-400">Path</th>
                                <th className="px-4 py-3 text-center text-sm font-medium text-cream-400">Status</th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-cream-400">Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ENDPOINTS.map((endpoint, idx) => {
                                const result = results[idx];
                                return (
                                    <tr key={endpoint.name} className="border-b border-charcoal-700/50 last:border-0">
                                        <td className="px-4 py-3 text-cream-100 font-medium">{endpoint.name}</td>
                                        <td className="px-4 py-3 text-cream-500 font-mono text-sm">{endpoint.path}</td>
                                        <td className="px-4 py-3 text-center">
                                            {!result ? (
                                                <span className="text-cream-600">—</span>
                                            ) : result.status === 'success' ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                                                    <CheckCircle className="w-3 h-3" />
                                                    200 OK
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-wine-500/20 text-wine-400 rounded text-xs">
                                                    <XCircle className="w-3 h-3" />
                                                    {result.statusCode || 'Error'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {result ? (
                                                <span className="inline-flex items-center gap-1 text-cream-400 text-sm">
                                                    <Clock className="w-3 h-3" />
                                                    {result.duration}ms
                                                </span>
                                            ) : (
                                                <span className="text-cream-600">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* JSON Output for Agent Parsing */}
                {testSummary && (
                    <div className="mt-6">
                        <h3 className="text-sm font-medium text-cream-400 mb-2">JSON Output (for agent parsing)</h3>
                        <pre className="bg-charcoal-950 border border-charcoal-700 rounded-lg p-4 text-xs text-cream-300 overflow-x-auto">
                            {JSON.stringify(testSummary, null, 2)}
                        </pre>
                    </div>
                )}

                {/* Timestamp */}
                <p className="text-center text-cream-600 text-xs mt-6">
                    {testSummary?.timestamp || new Date().toISOString()}
                </p>
            </div>
        </div>
    );
}
